import { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { createStakeDbPosition, readStakeDbPoolTitles } from '@/api/stake-db';
import { CreateStakeDbPositionRequest } from '@/types/stake-db';

const STAKE_STATE_OPTIONS = [
  { value: 0, label: 'Staking' },
  { value: 1, label: 'Unstaking' },
  { value: 2, label: 'Withdraw' },
];

export default function AddPositionForm() {
  const [titles, setTitles] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    walletAddress: '',
    stakeAmount: '',
    txHash: '',
    ticker: '',
    createdAt: '',
    stakeState: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const re = await readStakeDbPoolTitles();
        if (re.status === 200) {
          setTitles(re.data.titles || []);
        }
      } catch (error) {
        console.error('Failed to fetch pool titles:', error);
      }
    };
    fetchTitles();
  }, []);

  const handleChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<string | number>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Pool을 선택해주세요.');
      return;
    }
    if (!formData.walletAddress) {
      toast.error('지갑 주소를 입력해주세요.');
      return;
    }
    if (!formData.stakeAmount) {
      toast.error('스테이킹 수량을 입력해주세요.');
      return;
    }
    if (!formData.txHash) {
      toast.error('트랜잭션 해시를 입력해주세요.');
      return;
    }
    if (!formData.createdAt) {
      toast.error('등록일시를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const data: CreateStakeDbPositionRequest = {
        title: formData.title,
        walletAddress: formData.walletAddress,
        stakeAmount: formData.stakeAmount,
        txHash: formData.txHash,
        ticker: formData.ticker || '0',
        createdAt: formData.createdAt,
        stakeState: Number(formData.stakeState),
      };

      const re = await createStakeDbPosition(data);

      if (re.status === 200 || re.status === 201) {
        toast.success(re.data.message || '스테이킹 등록 성공');
        setFormData({
          title: '',
          walletAddress: '',
          stakeAmount: '',
          txHash: '',
          ticker: '',
          createdAt: '',
          stakeState: 0,
        });
      } else {
        toast.error(re.data?.message || '스테이킹 등록 실패');
      }
    } catch (error) {
      console.error('Create position error:', error);
      toast.error('스테이킹 등록 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            수동 스테이킹 등록
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            체인에 등록되었지만 DB에 누락된 스테이킹을 수동으로 등록합니다.
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Pool (Title)</InputLabel>
            <Select
              value={formData.title}
              label="Pool (Title)"
              onChange={handleSelectChange('title')}
            >
              <MenuItem value="">선택 안 함</MenuItem>
              {titles.map((title) => (
                <MenuItem key={title} value={title}>
                  {title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Stake State</InputLabel>
            <Select
              value={formData.stakeState}
              label="Stake State"
              onChange={handleSelectChange('stakeState')}
            >
              {STAKE_STATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Wallet Address</InputLabel>
            <OutlinedInput
              value={formData.walletAddress}
              label="Wallet Address"
              onChange={handleChange('walletAddress')}
              placeholder="0x..."
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Transaction Hash</InputLabel>
            <OutlinedInput
              value={formData.txHash}
              label="Transaction Hash"
              onChange={handleChange('txHash')}
              placeholder="0x..."
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Stake Amount</InputLabel>
            <OutlinedInput
              value={formData.stakeAmount}
              label="Stake Amount"
              onChange={handleChange('stakeAmount')}
              type="number"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Ticker (Token Price)</InputLabel>
            <OutlinedInput
              value={formData.ticker}
              label="Ticker (Token Price)"
              onChange={handleChange('ticker')}
              type="number"
              inputProps={{ step: '0.01' }}
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel shrink>Created At</InputLabel>
            <OutlinedInput
              value={formData.createdAt}
              label="Created At"
              onChange={handleChange('createdAt')}
              placeholder="2024-11-12 18:10:37"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : '스테이킹 등록'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
